import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

export interface PDFGenerationOptions {
  fileName?: string;
  title?: string;
  onStart?: () => void;
  onComplete?: () => void;
  onError?: (err: any) => void;
}

/**
 * Generates a clean multi-page PDF document directly from an HTML element
 * using html2canvas and jsPDF, saving it to the user's browser.
 */
export async function downloadPDFFromElement(
  elementOrId: HTMLElement | string,
  options: PDFGenerationOptions = {}
): Promise<void> {
  const { fileName = 'CNA_Study_Material.pdf', onStart, onComplete, onError } = options;

  if (onStart) onStart();

  let targetElement: HTMLElement | null = null;
  if (typeof elementOrId === 'string') {
    targetElement = document.getElementById(elementOrId);
  } else {
    targetElement = elementOrId;
  }

  if (!targetElement) {
    const errorMsg = 'PDF generation failed: target element not found.';
    console.error(errorMsg);
    if (onError) onError(new Error(errorMsg));
    if (onComplete) onComplete();
    return;
  }

  // Create an offscreen clone container for capturing
  const clone = targetElement.cloneNode(true) as HTMLElement;
  clone.style.position = 'fixed';
  clone.style.left = '-9999px';
  clone.style.top = '0';
  clone.style.width = '850px';
  clone.style.background = '#ffffff';
  clone.style.color = '#0f172a';
  clone.style.display = 'block';
  clone.style.visibility = 'visible';
  clone.style.opacity = '1';
  clone.style.zIndex = '-9999';

  // Remove hidden classes from root and descendants so html2canvas renders everything
  clone.classList.remove('hidden');
  const hiddenElements = clone.querySelectorAll('.hidden');
  hiddenElements.forEach(el => el.classList.remove('hidden'));

  // Force all images inside clone to fit on page nicely
  const cloneImages = clone.querySelectorAll('img');
  cloneImages.forEach(img => {
    img.style.maxHeight = '280px';
    img.style.maxWidth = '100%';
    img.style.objectFit = 'contain';
    img.style.pageBreakInside = 'avoid';
    img.style.breakInside = 'avoid';
  });

  document.body.appendChild(clone);

  try {
    const canvas = await html2canvas(clone, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    });

    const pdf = new jsPDF('p', 'mm', 'letter');
    const pdfWidth = pdf.internal.pageSize.getWidth(); // 215.9 mm
    const pdfHeight = pdf.internal.pageSize.getHeight(); // 279.4 mm
    const margin = 10; // 10mm margin
    const printableWidth = pdfWidth - margin * 2;
    const printableHeight = pdfHeight - margin * 2;

    const imgWidth = canvas.width;
    const imgHeight = canvas.height;

    const cloneRect = clone.getBoundingClientRect();
    const scaleRatio = canvas.width / (cloneRect.width || 850);

    const breakNodes = clone.querySelectorAll('.print-page-break, .print-no-cut, img, table, tr');
    const breakPositions: { top: number; bottom: number; isForceBreak: boolean }[] = [];

    breakNodes.forEach(node => {
      const rect = node.getBoundingClientRect();
      const top = (rect.top - cloneRect.top) * scaleRatio;
      const bottom = (rect.bottom - cloneRect.top) * scaleRatio;
      const isForceBreak = node.classList.contains('print-page-break');
      if (bottom > top) {
        breakPositions.push({ top, bottom, isForceBreak });
      }
    });

    const sliceCanvasHeight = (imgWidth * printableHeight) / printableWidth;
    let yOffset = 0;
    let pageIndex = 0;

    while (yOffset < imgHeight) {
      if (pageIndex > 0) {
        pdf.addPage();
      }

      let defaultTargetY = yOffset + sliceCanvasHeight;
      let chosenTargetY = defaultTargetY;

      if (defaultTargetY < imgHeight) {
        // 1. Check if an explicit forced page break exists between yOffset and defaultTargetY
        const forceBreakNode = breakPositions.find(
          b => b.isForceBreak && b.top > yOffset + 40 && b.top <= defaultTargetY
        );

        if (forceBreakNode) {
          chosenTargetY = forceBreakNode.top;
        } else {
          // 2. Check if defaultTargetY slices through an element with print-no-cut or img/table
          const straddledNode = breakPositions.find(
            b => b.top < defaultTargetY && b.bottom > defaultTargetY && b.top > yOffset + 80
          );
          if (straddledNode) {
            chosenTargetY = straddledNode.top;
          }
        }
      } else {
        chosenTargetY = imgHeight;
      }

      let currentSliceHeight = chosenTargetY - yOffset;
      if (currentSliceHeight <= 0) {
        chosenTargetY = defaultTargetY;
        currentSliceHeight = chosenTargetY - yOffset;
      }

      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = imgWidth;
      const sliceH = Math.min(currentSliceHeight, imgHeight - yOffset);
      pageCanvas.height = sliceH;

      const pageCtx = pageCanvas.getContext('2d');
      if (pageCtx) {
        pageCtx.fillStyle = '#ffffff';
        pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        pageCtx.drawImage(
          canvas,
          0,
          yOffset,
          imgWidth,
          sliceH,
          0,
          0,
          imgWidth,
          sliceH
        );
      }

      const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.95);
      const renderedSliceHeightMM = (sliceH * printableWidth) / imgWidth;

      pdf.addImage(pageImgData, 'JPEG', margin, margin, printableWidth, renderedSliceHeightMM);

      yOffset = chosenTargetY;
      pageIndex++;
    }

    const finalName = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
    pdf.save(finalName);
    if (onComplete) onComplete();
  } catch (err) {
    console.error('Error generating PDF:', err);
    if (onError) onError(err);
    if (onComplete) onComplete();
  } finally {
    if (document.body.contains(clone)) {
      document.body.removeChild(clone);
    }
  }
}
